import * as React from 'react';
import { reportIssue } from '@/lib/telemetry';
import { Button } from './ui/button';
import { Mark } from './layout/mark';

/* ---------------------------------------------------------------------------
   The last line of defence.

   A render fault anywhere below this boundary would otherwise white-screen the
   console, and the reviewer's most likely next move is a reload — which loses
   the stream cursor, the scroll position in the diff, and any half-written
   request-changes note.

   So the boundary keeps the chassis, reports the fault as drift telemetry, and
   offers exactly two moves: try the same screen again, or go back to the queue.
   It deliberately does NOT print the error text. On this surface an error string
   can carry a repository path or a SHA out of an agent's output, and while an
   expert is allowed to read those, a screenshot of a crash pasted into a shared
   ticket is a different audience.
--------------------------------------------------------------------------- */

interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    reportIssue({
      kind: 'render_error',
      token: error.name || 'render_error',
      context: { message: error.message.slice(0, 120) },
    });
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-ground px-6">
        <div className="w-full max-w-md">
          <Mark className="h-6 w-6 text-ink" />
          <h1 className="mt-5 text-xl font-semibold tracking-[-0.015em] text-ink">This screen stopped drawing</h1>
          <p className="mt-2 text-sm leading-[19px] text-ink-2">
            Something in the renderer failed. Nothing you did has been lost server-side — no claim was released, no run
            was cancelled and no merge was started. The fault has been reported.
          </p>
          {import.meta.env.DEV ? (
            <pre className="mt-4 max-h-48 overflow-auto border border-danger bg-danger-soft px-2 py-1.5 font-mono text-tiny leading-[16px] text-danger">
              {this.state.error.stack ?? this.state.error.message}
            </pre>
          ) : null}
          <div className="mt-5 flex flex-wrap gap-2">
            <Button variant="primary" onClick={() => this.setState({ error: null })}>
              Try this screen again
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                window.location.assign('/queue');
              }}
            >
              Back to the queue
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
